import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import type { PointerEvent as ReactPointerEvent } from "react";
import {
  Alert,
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalDialog,
  Option,
  Select,
  Stack,
  Switch,
  Textarea,
  Typography,
} from "@mui/joy";
import type { Photo, Tag } from "../../types";
import { apiClient } from "../../lib/api";
import { TagPicker } from "../tags/TagPicker";
import {
  AnnotationNavigationGuard,
  type PendingAnnotationNavigation,
} from "./AnnotationNavigationGuard";
import { AnnotationLayer } from "./AnnotationLayer";
import { useAnnotationDoc } from "../../shared/photo-annotations/useAnnotationDoc";
import { DEFAULT_REFERENCE_WIDTH } from "../../shared/photo-annotations/annotationMigrate";
import {
  applyHandleDrag,
  containRect,
  decimateToMax,
  handlesFor,
  hitTestTopmost,
  moveItem,
  projectX,
  projectY,
  simplifyRDP,
  unprojectX,
  unprojectY,
} from "../../shared/photo-annotations/annotationGeometry";
import {
  DEFAULT_COLOR,
  DEFAULT_SIZE,
  DEFAULT_WIDTH,
  MAX_FREEHAND_POINTS,
  MAX_TEXT_LENGTH,
  PALETTE,
  RDP_EPSILON,
  SIZE_STEPS,
  WIDTH_STEPS,
  makeAnnotationId,
  normalizeText,
} from "../../shared/photo-annotations/annotationSchema";
import type {
  AnnotationItem,
  NPoint,
} from "../../shared/photo-annotations/annotationSchema";

type Tool = "select" | "pan" | AnnotationItem["kind"];
type Gesture = {
  pointer: number;
  start: NPoint;
  item?: AnnotationItem;
  handle?: number;
  draft?: AnnotationItem;
  pan?: { x: number; y: number };
  client: NPoint;
};
const tools: [Tool, string][] = [
  ["select", "Select"],
  ["pan", "Pan"],
  ["arrow", "Arrow"],
  ["ellipse", "Ellipse"],
  ["freehand", "Draw"],
  ["text", "Text"],
];
const colors = ["Red", "Yellow", "Green", "Blue", "White", "Black"];

export function PhotoAnnotationEditor({
  photo,
  availableTags = [],
  onCreateTag,
  onClose,
  onSaved,
}: {
  photo: Photo;
  availableTags?: Tag[];
  onCreateTag?: (name: string, color: string) => Promise<Tag>;
  onClose: () => void;
  onSaved: (photo: Photo) => void;
}) {
  const [latest, setLatest] = useState(photo);
  const [description, setDescription] = useState(photo.description || "");
  const [tagIds, setTagIds] = useState((photo.tags || []).map((tag) => tag.id));
  const [createdTags, setCreatedTags] = useState<Tag[]>([]);
  const tags = Array.from(
    new Map(
      [...(latest.tags || []), ...availableTags, ...createdTags].map((tag) => [tag.id, tag]),
    ).values(),
  );
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [retry, setRetry] = useState(0);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const pendingNavigation = useRef<PendingAnnotationNavigation | null>(null);
  const onNavigationBlocked = useCallback(
    (navigation: PendingAnnotationNavigation) => {
      pendingNavigation.current = navigation;
      setConfirmClose(true);
    },
    [],
  );
  const keepEditing = () => {
    pendingNavigation.current?.reset();
    pendingNavigation.current = null;
    setConfirmClose(false);
  };
  const leave = () => {
    const navigation = pendingNavigation.current;
    pendingNavigation.current = null;
    navigation?.proceed();
    onClose();
  };
  const [imageSize, setImageSize] = useState({ w: 0, h: 0 });
  const [imageError, setImageError] = useState(false);
  const [box, setBox] = useState({ w: 1, h: 1 });
  const [stage, setStage] = useState<HTMLDivElement | null>(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [tool, setTool] = useState<Tool>("select");
  const [color, setColor] = useState<string>(DEFAULT_COLOR);
  const [width, setWidth] = useState<number>(DEFAULT_WIDTH);
  const [textSize, setTextSize] = useState<number>(DEFAULT_SIZE);
  const [textEdit, setTextEdit] = useState<{
    at: NPoint;
    id?: string;
    value: string;
  } | null>(null);
  const [draft, setDraft] = useState<AnnotationItem | null>(null);
  const gesture = useRef<Gesture | null>(null);
  const doc = useAnnotationDoc({
    initial: latest.annotations,
    imageAspect: imageSize.w ? imageSize.w / imageSize.h : null,
    screenWidth: DEFAULT_REFERENCE_WIDTH,
  });
  const selected = doc.items.find((item) => item.id === doc.selectedId);
  const activeWidth = selected && "width" in selected ? selected.width : width;
  const activeTextSize = selected?.kind === "text" ? selected.size : textSize;
  const ready = !loading && !loadError && imageSize.w > 0 && !imageError;
  const descriptionDirty = description !== (latest.description || "");
  const latestTagIds = (latest.tags || []).map((tag) => tag.id);
  const tagsDirty =
    tagIds.length !== latestTagIds.length || tagIds.some((id) => !latestTagIds.includes(id));
  const dirty =
    doc.isDirty || descriptionDirty || tagsDirty || draft !== null || textEdit !== null;
  const fit = containRect(
    box.w,
    box.h,
    imageSize.w ? imageSize.w / imageSize.h : null,
  );
  const rect = {
    x: (box.w - fit.w * zoom) / 2 + pan.x,
    y: (box.h - fit.h * zoom) / 2 + pan.y,
    w: fit.w * zoom,
    h: fit.h * zoom,
  };

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError(false);
    apiClient
      .get<Photo>(`/api/photos/${photo.id}/`)
      .then((response) => {
        if (active) {
          setLatest(response.data);
          setDescription(response.data.description || "");
          setTagIds((response.data.tags || []).map((tag) => tag.id));
        }
      })
      .catch(() => {
        if (active) setLoadError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [photo.id, retry]);
  useLayoutEffect(() => {
    const element = stage;
    if (!element) return;
    const measure = () =>
      setBox({ w: element.clientWidth, h: element.clientHeight });
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [stage]);
  useEffect(() => {
    if (!dirty) return;
    const prevent = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", prevent);
    return () => window.removeEventListener("beforeunload", prevent);
  }, [dirty]);

  const requestClose = () => {
    if (savingRef.current) return;
    if (dirty) setConfirmClose(true);
    else onClose();
  };
  const save = async () => {
    if (savingRef.current || !ready || draft || textEdit) return;
    savingRef.current = true;
    setSaving(true);
    setError("");
    try {
      const response = await apiClient.patch<Photo>(
        `/api/photos/${photo.id}/`,
        {
          ...(doc.isDirty ? { annotations: doc.doc } : {}),
          ...(descriptionDirty ? { description } : {}),
          ...(tagsDirty ? { tag_ids: tagIds } : {}),
        },
      );
      doc.markSaved();
      onSaved({ ...latest, ...response.data });
      leave();
    } catch {
      setError(
        "Could not save the photo. Your edits are still here. Please try again.",
      );
      keepEditing();
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };
  const styleItem = (changes: Partial<AnnotationItem>) => {
    if (selected) doc.commitItem({ ...selected, ...changes } as AnnotationItem);
  };
  const changeZoom = (next: number) => {
    setZoom(Math.max(1, Math.min(4, next)));
    setPan({ x: 0, y: 0 });
  };
  const point = (event: ReactPointerEvent): NPoint => {
    const bounds = stage!.getBoundingClientRect();
    return [
      unprojectX(event.clientX - bounds.left, rect),
      unprojectY(event.clientY - bounds.top, rect),
    ];
  };
  const begin = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!ready || saving || textEdit || gesture.current || event.button !== 0)
      return;
    const bounds = event.currentTarget.getBoundingClientRect();
    const sx = event.clientX - bounds.left,
      sy = event.clientY - bounds.top;
    if (
      tool !== "pan" &&
      (sx < rect.x ||
        sy < rect.y ||
        sx > rect.x + rect.w ||
        sy > rect.y + rect.h)
    )
      return;
    event.preventDefault();
    event.currentTarget.focus();
    event.currentTarget.setPointerCapture(event.pointerId);
    const start = point(event);
    const next: Gesture = {
      pointer: event.pointerId,
      start,
      client: [event.clientX, event.clientY],
    };
    if (tool === "pan") next.pan = pan;
    else if (tool === "select") {
      const handle = selected
        ? handlesFor(selected).findIndex(
            (p) =>
              Math.hypot(
                projectX(p[0], rect) - sx,
                projectY(p[1], rect) - sy,
              ) <= 16,
          )
        : -1;
      const id =
        handle >= 0
          ? selected!.id
          : hitTestTopmost(
              doc.items,
              sx,
              sy,
              rect,
              event.pointerType === "mouse" ? 8 : 22,
            );
      doc.select(id);
      next.item = doc.items.find((item) => item.id === id);
      if (handle >= 0) next.handle = handle;
    } else if (tool === "text") {
      setTextEdit({ at: start, value: "" });
      return;
    } else {
      const stamp = new Date().toISOString();
      const base = {
        id: makeAnnotationId(tool),
        color,
        width,
        createdAt: stamp,
        updatedAt: stamp,
      };
      next.draft =
        tool === "arrow"
          ? { ...base, kind: "arrow", from: start, to: start }
          : tool === "ellipse"
            ? { ...base, kind: "ellipse", center: start, rx: 0, ry: 0 }
            : { ...base, kind: "freehand", points: [start] };
      setDraft(next.draft);
    }
    gesture.current = next;
  };
  const move = (event: ReactPointerEvent<HTMLDivElement>) => {
    const current = gesture.current;
    if (!current || current.pointer !== event.pointerId) return;
    const p = point(event);
    if (current.pan)
      setPan({
        x: current.pan.x + event.clientX - current.client[0],
        y: current.pan.y + event.clientY - current.client[1],
      });
    else if (current.item) {
      current.draft =
        current.handle !== undefined
          ? applyHandleDrag(current.item, current.handle, ...p)
          : moveItem(
              current.item,
              p[0] - current.start[0],
              p[1] - current.start[1],
            );
      setDraft(current.draft);
    } else if (current.draft) {
      const item = current.draft;
      if (item.kind === "arrow") current.draft = { ...item, to: p };
      if (item.kind === "ellipse")
        current.draft = {
          ...item,
          center: [
            (current.start[0] + p[0]) / 2,
            (current.start[1] + p[1]) / 2,
          ],
          rx: Math.abs(p[0] - current.start[0]) / 2,
          ry: Math.abs(p[1] - current.start[1]) / 2,
        };
      if (item.kind === "freehand")
        current.draft = {
          ...item,
          points: decimateToMax([...item.points, p], MAX_FREEHAND_POINTS * 2),
        };
      setDraft(current.draft!);
    }
  };
  const finish = (
    event: ReactPointerEvent<HTMLDivElement>,
    cancelled = false,
  ) => {
    const current = gesture.current;
    if (!current || current.pointer !== event.pointerId) return;
    if (!cancelled) {
      move(event);
      let item = current.draft;
      if (item?.kind === "freehand")
        item = {
          ...item,
          points: decimateToMax(
            simplifyRDP(item.points, RDP_EPSILON),
            MAX_FREEHAND_POINTS,
          ),
        };
      if (
        item &&
        (current.item ||
          Math.hypot(
            event.clientX - current.client[0],
            event.clientY - current.client[1],
          ) >= 3)
      ) {
        if (current.item) doc.commitItem(item);
        else doc.addItem(item);
      }
    }
    gesture.current = null;
    setDraft(null);
    if (event.currentTarget.hasPointerCapture(event.pointerId))
      event.currentTarget.releasePointerCapture(event.pointerId);
  };
  const applyText = () => {
    if (!textEdit) return;
    const text = normalizeText(textEdit.value);
    if (!text) return;
    const existing = doc.items.find((item) => item.id === textEdit.id);
    if (existing?.kind === "text") doc.commitItem({ ...existing, text });
    else {
      const stamp = new Date().toISOString();
      doc.addItem({
        id: makeAnnotationId("text"),
        kind: "text",
        at: textEdit.at,
        text,
        color,
        size: textSize,
        halo: true,
        createdAt: stamp,
        updatedAt: stamp,
      });
    }
    setTextEdit(null);
    setTool("select");
  };
  const displayItems = draft
    ? doc.items.some((item) => item.id === draft.id)
      ? doc.items.map((item) => (item.id === draft.id ? draft : item))
      : [...doc.items, draft]
    : doc.items;

  return (
    <Modal open onClose={requestClose}>
      <ModalDialog
        aria-labelledby="annotation-editor-title"
        sx={{
          width: "calc(100% - 32px)",
          height: "calc(100dvh - 32px)",
          maxWidth: 1800,
          maxHeight: "calc(100dvh - 32px)",
          minWidth: 0,
          p: { xs: 1.5, sm: 2 },
          gap: 2,
          overflow: "auto",
          borderRadius: "lg",
        }}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.stopPropagation();
            if (textEdit) setTextEdit(null);
            else if (confirmClose) keepEditing();
            else requestClose();
            return;
          }
          if (
            saving ||
            textEdit ||
            gesture.current ||
            (event.target as HTMLElement).closest(
              'input, textarea, [role="combobox"]',
            )
          )
            return;
          if (
            (event.ctrlKey || event.metaKey) &&
            event.key.toLowerCase() === "z"
          ) {
            event.preventDefault();
            if (event.shiftKey) doc.redo();
            else doc.undo();
          }
          if (
            (event.key === "Delete" || event.key === "Backspace") &&
            selected
          ) {
            event.preventDefault();
            doc.removeItem(selected.id);
          }
        }}
      >
        <AnnotationNavigationGuard
          dirty={dirty || saving}
          onBlocked={onNavigationBlocked}
        />
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          gap={1}
          flexWrap="wrap"
        >
          <Box>
            <Typography id="annotation-editor-title" level="h4">
              Edit Photo
            </Typography>
            <Typography level="body-sm">
              {latest.description || `Photo ${photo.id}`} ·{" "}
              {dirty ? "Unsaved changes" : "All changes saved"}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button variant="plain" disabled={saving} onClick={requestClose}>
              Close
            </Button>
            <Button
              loading={saving}
              disabled={!ready || !dirty || !!draft || !!textEdit}
              onClick={save}
            >
              Save changes
            </Button>
          </Stack>
        </Stack>
        {loading && <Alert>Loading latest photo…</Alert>}
        {loadError && (
          <Alert
            color="danger"
            endDecorator={
              <Button onClick={() => setRetry((n) => n + 1)}>Retry</Button>
            }
          >
            Could not load the latest photo. Retry before editing.
          </Alert>
        )}
        {error && <Alert color="danger">{error}</Alert>}
        {imageError && (
          <Alert color="danger">
            The photo could not load. Close and reopen it to try again.
          </Alert>
        )}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "minmax(0, 1fr)",
              md: "232px minmax(0, 1fr) 280px",
            },
            gap: 2,
            flex: 1,
            minHeight: { md: 0 },
          }}
        >
          <Stack spacing={2} sx={{ minWidth: 0, overflowY: { md: "auto" }, p: 1 }}>
            <Typography level="title-sm">Annotation tools</Typography>
            <Box
              component="fieldset"
              disabled={!ready || saving || !!textEdit}
              sx={{
                m: 0,
                p: 0,
                border: 0,
                display: "grid",
                gridTemplateColumns: { xs: "repeat(3, 1fr)", md: "repeat(2, 1fr)" },
                gap: 1,
              }}
            >
              {tools.map(([value, label]) => (
                <Button
                  key={value}
                  size="sm"
                  variant={tool === value ? "solid" : "outlined"}
                  aria-pressed={tool === value}
                  onClick={() => {
                    setTool(value);
                    if (value !== "select") doc.select(null);
                  }}
                >
                  {label}
                </Button>
              ))}
              <Button
                size="sm"
                variant="soft"
                disabled={!doc.canUndo}
                onClick={doc.undo}
              >
                Undo
              </Button>
              <Button
                size="sm"
                variant="soft"
                disabled={!doc.canRedo}
                onClick={doc.redo}
              >
                Redo
              </Button>
              <Button
                size="sm"
                color="danger"
                variant="soft"
                disabled={!selected}
                onClick={() => selected && doc.removeItem(selected.id)}
              >
                Delete selected
              </Button>
              <Button
                size="sm"
                variant="outlined"
                aria-label="Zoom out"
                disabled={zoom <= 1}
                onClick={() => changeZoom(zoom - 0.25)}
              >
                −
              </Button>
              <Button size="sm" variant="plain" onClick={() => changeZoom(1)}>
                Fit · {Math.round(zoom * 100)}%
              </Button>
              <Button
                size="sm"
                variant="outlined"
                aria-label="Zoom in"
                disabled={zoom >= 4}
                onClick={() => changeZoom(zoom + 0.25)}
              >
                +
              </Button>
            </Box>
            <Box
              component="fieldset"
              disabled={!ready || saving || !!textEdit}
              sx={{
                m: 0,
                p: 0,
                border: 0,
                display: "flex",
                flexWrap: "wrap",
                alignItems: "end",
                gap: 1,
              }}
            >
              <FormControl size="sm">
                <FormLabel>Color</FormLabel>
                <Stack direction="row" gap={0.5} flexWrap="wrap">
                  {PALETTE.map((value, i) => (
                    <Button
                      key={value}
                      size="sm"
                      aria-label={colors[i]}
                      aria-pressed={(selected?.color || color) === value}
                      onClick={() => {
                        setColor(value);
                        styleItem({ color: value });
                      }}
                      sx={{
                        minWidth: 30,
                        width: 30,
                        height: 30,
                        p: 0,
                        backgroundColor: value,
                        border: "2px solid",
                        borderColor:
                          (selected?.color || color) === value
                            ? "primary.500"
                            : "neutral.400",
                        boxShadow:
                          (selected?.color || color) === value
                            ? "0 0 0 2px var(--joy-palette-primary-500)"
                            : "none",
                        '&:hover, &:active, &[aria-pressed="true"]': {
                          backgroundColor: value,
                        },
                      }}
                    />
                  ))}
                </Stack>
              </FormControl>
              <FormControl size="sm">
                <FormLabel>Stroke</FormLabel>
                <Select
                  aria-label="Stroke width"
                  disabled={selected?.kind === "text"}
                  value={activeWidth}
                  onChange={(_, value) => {
                    if (value) {
                      setWidth(value);
                      styleItem({ width: value });
                    }
                  }}
                >
                  {!WIDTH_STEPS.some((value) => value === activeWidth) && (
                    <Option value={activeWidth}>Current</Option>
                  )}
                  {WIDTH_STEPS.map((value, i) => (
                    <Option key={value} value={value}>
                      {["Thin", "Medium", "Thick"][i]}
                    </Option>
                  ))}
                </Select>
              </FormControl>
              {(tool === "text" || selected?.kind === "text") && (
                <>
                  <FormControl size="sm">
                    <FormLabel>Text size</FormLabel>
                    <Select
                      aria-label="Text size"
                      value={activeTextSize}
                      onChange={(_, value) => {
                        if (value) {
                          setTextSize(value);
                          if (selected?.kind === "text") styleItem({ size: value });
                        }
                      }}
                    >
                      {!SIZE_STEPS.some((value) => value === activeTextSize) && (
                        <Option value={activeTextSize}>Current</Option>
                      )}
                      {SIZE_STEPS.map((value, i) => (
                        <Option key={value} value={value}>
                          {["Small", "Medium", "Large", "Extra large"][i]}
                        </Option>
                      ))}
                    </Select>
                  </FormControl>
                  {selected?.kind === "text" && (
                    <>
                      <Button
                        size="sm"
                        variant="soft"
                        onClick={() =>
                          setTextEdit({
                            at: selected.at,
                            id: selected.id,
                            value: selected.text,
                          })
                        }
                      >
                        Edit text
                      </Button>
                      <Switch
                        checked={selected.halo}
                        onChange={(event) =>
                          styleItem({ halo: event.target.checked })
                        }
                        endDecorator="Text outline"
                      />
                    </>
                  )}
                </>
              )}
            </Box>
          </Stack>
          <Stack spacing={1} sx={{ minWidth: 0, minHeight: { xs: 340, md: 0 } }}>
            <Box
              ref={setStage}
              role="application"
              aria-label="Photo annotation canvas"
              tabIndex={0}
              sx={{
                flex: 1,
                minHeight: { xs: 300, md: 240 },
                borderRadius: "md",
                position: "relative",
                overflow: "hidden",
                bgcolor: "#111",
                touchAction: "none",
                userSelect: "none",
                cursor:
                  tool === "pan"
                    ? "grab"
                    : tool === "select"
                      ? "default"
                      : "crosshair",
              }}
              onPointerDown={begin}
              onPointerMove={move}
              onPointerUp={(event) => finish(event)}
              onPointerCancel={(event) => finish(event, true)}
              onLostPointerCapture={(event) => finish(event, true)}
            >
              <img
                src={latest.image_url || latest.image}
                alt={latest.description || "Photo to annotate"}
                draggable={false}
                onLoad={(event) => {
                  setImageSize({
                    w: event.currentTarget.naturalWidth,
                    h: event.currentTarget.naturalHeight,
                  });
                  setImageError(false);
                }}
                onError={() => setImageError(true)}
                style={{
                  position: "absolute",
                  left: rect.x,
                  top: rect.y,
                  width: rect.w,
                  height: rect.h,
                  pointerEvents: "none",
                }}
              />
              {ready && (
                <AnnotationLayer
                  doc={{ schema: 2, items: displayItems }}
                  rect={rect}
                  width={box.w}
                  height={box.h}
                  selectedId={doc.selectedId}
                />
              )}
              {ready && selected && tool === "select" && (
                <svg
                  aria-hidden="true"
                  width={box.w}
                  height={box.h}
                  style={{ position: "absolute", inset: 0, pointerEvents: "none" }}
                >
                  {handlesFor(draft || selected).map((p, i) => (
                    <circle
                      key={i}
                      cx={projectX(p[0], rect)}
                      cy={projectY(p[1], rect)}
                      r={7}
                      fill="#fff"
                      stroke="#FFD700"
                      strokeWidth={3}
                    />
                  ))}
                </svg>
              )}
            </Box>
            <Typography level="body-xs">
              {tool === "select"
                ? "Select a mark to move it. Drag its handles to resize. Use Delete or Ctrl/⌘ Z to undo."
                : tool === "pan"
                  ? "Drag to move the photo. Use Fit to reset the view."
                  : tool === "text"
                    ? "Click or tap the photo to add a label."
                    : "Drag on the photo to draw. Use Select to edit a mark."}
            </Typography>
          </Stack>
          <Stack spacing={2} sx={{ minWidth: 0, overflowY: { md: "auto" }, p: 1 }}>
            <TagPicker
              value={tagIds}
              onChange={setTagIds}
              availableTags={tags}
              disabled={!ready || saving}
              onCreateTag={onCreateTag ? async (name, color) => {
                try {
                  const tag = await onCreateTag(name, color);
                  setCreatedTags((current) => [...current, tag]);
                  return tag;
                } catch (error) {
                  setError("Could not create the tag. Please try again.");
                  throw error;
                }
              } : undefined}
              placeholder="Search or create tags..."
            />
            <FormControl disabled={!ready || saving}>
              <FormLabel>Description</FormLabel>
              <Textarea
                minRows={5}
                maxRows={12}
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                placeholder="Add a description..."
              />
            </FormControl>
          </Stack>
        </Box>
        <Modal open={!!textEdit} onClose={() => setTextEdit(null)}>
          <ModalDialog aria-labelledby="annotation-text-title">
            <Typography id="annotation-text-title" level="h4">
              {textEdit?.id ? "Edit text" : "Add text"}
            </Typography>
            <FormControl>
              <FormLabel>Label</FormLabel>
              <Input
                autoFocus
                value={textEdit?.value || ""}
                slotProps={{ input: { maxLength: MAX_TEXT_LENGTH } }}
                onChange={(event) =>
                  setTextEdit(
                    (value) => value && { ...value, value: event.target.value },
                  )
                }
                onKeyDown={(event) => {
                  event.stopPropagation();
                  if (event.key === "Enter") applyText();
                }}
              />
            </FormControl>
            <Stack direction="row" spacing={1}>
              <Button variant="plain" onClick={() => setTextEdit(null)}>
                Cancel
              </Button>
              <Button
                disabled={!normalizeText(textEdit?.value)}
                onClick={applyText}
              >
                Apply text
              </Button>
            </Stack>
          </ModalDialog>
        </Modal>
        <Modal open={confirmClose} onClose={() => !saving && keepEditing()}>
          <ModalDialog aria-labelledby="annotation-unsaved-title">
            <Typography id="annotation-unsaved-title" level="h4">
              Save your photo changes?
            </Typography>
            <Typography>Your changes have not been saved.</Typography>
            <Stack direction="row" flexWrap="wrap" gap={1}>
              <Button variant="plain" disabled={saving} onClick={keepEditing}>
                Keep editing
              </Button>
              <Button
                color="danger"
                variant="soft"
                disabled={saving}
                onClick={leave}
              >
                Discard
              </Button>
              <Button
                loading={saving}
                disabled={!ready || !!draft || !!textEdit}
                onClick={save}
              >
                Save & close
              </Button>
            </Stack>
          </ModalDialog>
        </Modal>
      </ModalDialog>
    </Modal>
  );
}
